import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Select } from '@ngxs/store';
import { TemplateVariable } from 'app/messages/message-service/template-variable.model';
import { EmailTemplate } from 'app/models/dto/email-template.model';
import { EmailTemplateState } from 'app/store/lender-user/email-templates/email-templates.state';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-email-template-dialog',
  templateUrl: './email-template-dialog.component.html',
  styleUrls: ['./email-template-dialog.component.scss']
})
export class EmailTemplateDialogComponent implements OnInit, OnDestroy {

  @Select(EmailTemplateState.loading) loading$: Observable<boolean>;
  @Select(EmailTemplateState.variables) variables$: TemplateVariable[];

  @Output() save = new EventEmitter<EmailTemplate>();

  quillEditorRef;
  selectionRange: { index: number, length: number };
  changeCount = 0;
  saveButtonDisabled = true;
  draft: string;
  original = {
    content: '',
    title: '',
    subject: ''
  };

  constructor(@Inject(MAT_DIALOG_DATA) public template: EmailTemplate) { }

  ngOnInit(): void {
    this.loadDraft();
    this.saveOriginal();
  }

  ngOnDestroy() {
    this.restoreOriginal();
  }

  handleEditorCreated(editorInstance: any): void {
    this.quillEditorRef = editorInstance;
    this.quillEditorRef.focus();
  }

  handleEmoji($event): void {
    this.quillEditorRef.focus();
    const index = this.selectionRange.index || 0;
    this.quillEditorRef.insertText(index, $event.emoji.native, 'user');
    this.quillEditorRef.setSelection(index + 2, 0, 'user');
  }

  handleSelectionChange(selectionRange): void {
    this.rememberSelectionRange(selectionRange);
  }

  rememberSelectionRange(selectionRange) {
    if (!selectionRange) {
      return;
    }
    this.selectionRange = selectionRange;
  }

  handleContentChange() {
    if (!this.quillEditorRef) {
      return;
    }
    this.saveDraft();
    this.changeCount++;
    if (this.selectionRange) {
      this.quillEditorRef.focus();
      this.quillEditorRef.setSelection(this.quillEditorRef.getSelection().index || 0, 0, 'user');
      this.rememberSelectionRange(this.quillEditorRef.getSelection());
    } else {
      this.rememberSelectionRange(this.quillEditorRef.getSelection());
    }
    this.toggleSaveDisable();
  }

  titleSubjectChange() {
    this.toggleSaveDisable();
    this.saveDraft();
  }

  handleEnterSelectionLocation() {
    this.quillEditorRef.focus();
    this.quillEditorRef.setSelection(this.quillEditorRef.getSelection().index || 0, 0, 'user');
    this.rememberSelectionRange(this.quillEditorRef.getSelection());
  }

  toggleSaveDisable() {
    if (
      this.template?.content
      && this.template?.content !== ''
      && this.template?.title !== ''
      && this.template?.subject !== ''
    ) {
      this.saveButtonDisabled = false;
    } else {
      this.saveButtonDisabled = true;
    }
  }

  saveTemplate() {
    this.save.emit(this.template);
    this.clearDraft();
  }

  saveOriginal() {
    if (this.template.id) {
      this.original.content = this.template.content;
      this.original.title = this.template.title;
      this.original.subject = this.template.subject;
    }
  }

  restoreOriginal() {
    if (this.template.id) {
      this.template.content = this.original.content;
      this.template.title = this.original.title;
      this.template.subject = this.original.subject;
    }
  }

  handleInsertVariable(variable: TemplateVariable) {
    this.quillEditorRef.focus();
    const index = this.selectionRange.index || 0;
    this.quillEditorRef.insertText(index, `{${variable.variable}}`, 'user');
    this.quillEditorRef.setSelection(index + variable.variable.length + 3, 0, 'user');
  }

  loadDraft(): void {
    if (!this.template.id) {
      const json = localStorage.getItem('userTemplateDraft') || null;
      this.template.content = json ? JSON.parse(json).content : '';
      this.template.title = json ? JSON.parse(json).title : '';
      this.template.subject = json ? JSON.parse(json).subject : '';
    }
  }

  saveDraft() {
    if (!this.template.id) {
      const templateDraft = {
        content: this.template.content,
        title: this.template.title,
        subject: this.template.subject
      };
      localStorage.setItem('userTemplateDraft', JSON.stringify(templateDraft));
    }
  }

  clearDraft() {
    localStorage.removeItem('userTemplateDraft');
  }
}
